import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowUpRight, Plus, Search, Pencil, Trash2, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { value: 'trip_payment', label: 'Trip Payment' },
  { value: 'contract', label: 'Contract' },
  { value: 'refund', label: 'Refund' },
  { value: 'other', label: 'Other' },
];

const vatTypes = [
  { value: 'inclusive', label: 'VAT Inclusive (15%)' },
  { value: 'exclusive', label: 'VAT Exclusive (add 15%)' },
  { value: 'no_vat', label: 'No VAT' },
];

export default function Income() {
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [financialYears, setFinancialYears] = useState([]);
  const [filterYear, setFilterYear] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: 'other',
    vat_type: 'inclusive',
    vehicle_registration: '',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  useEffect(() => {
    fetchIncome();
  }, [filterCategory, filterYear]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      setFinancialYears(response.data.financial_years || []);
    } catch (error) {
      console.error('Failed to load financial years');
    }
  };

  const fetchIncome = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterYear !== 'all') params.financial_year = filterYear;
      const response = await axios.get(`${API_URL}/finance/income`, { params });
      setIncome(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load income'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (editingIncome) {
        await axios.put(`${API_URL}/finance/income/${editingIncome.id}`, payload);
        toast.success('Income updated');
      } else {
        await axios.post(`${API_URL}/finance/income`, payload);
        toast.success('Income added');
      }

      setShowDialog(false);
      resetForm();
      fetchIncome();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save income'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete income "${item.description}"?`)) return;

    try {
      await axios.delete(`${API_URL}/finance/income/${item.id}`);
      toast.success('Income deleted');
      fetchIncome();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete income'));
    }
  };

  const openEditDialog = (item) => {
    setEditingIncome(item);
    setForm({
      date: item.date,
      description: item.description,
      amount: item.amount.toString(),
      category: item.category,
      vat_type: item.vat_type || 'inclusive',
      vehicle_registration: item.vehicle_registration || '',
      reference: item.reference || '',
      notes: item.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingIncome(null);
    setForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      category: 'other',
      vat_type: 'inclusive',
      vehicle_registration: '',
      reference: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const filteredIncome = income.filter(item =>
    item.description?.toLowerCase().includes(search.toLowerCase()) ||
    item.reference?.toLowerCase().includes(search.toLowerCase())
  );

  const totalAmount = filteredIncome.reduce((sum, item) => sum + (item.net_excl_vat || item.amount || 0), 0);
  const totalVAT = filteredIncome.reduce((sum, item) => sum + (item.vat_amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="income-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Income</h1>
            <p className="text-muted-foreground mt-1">Manual income entries with VAT tracking</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-green-600 hover:bg-green-700" data-testid="add-income-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Income
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search income..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Financial Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {financialYears.map(fy => (
                    <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">Net Excl VAT</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totalAmount)}</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">VAT Amount</p>
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalVAT)}</p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Total Incl VAT</p>
            <p className="text-2xl font-bold">{formatCurrency(totalAmount + totalVAT)}</p>
          </div>
        </div>

        {/* Income Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : filteredIncome.length === 0 ? (
              <div className="text-center py-12">
                <ArrowUpRight className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No income found</h3>
                <p className="text-muted-foreground mb-4">Start by adding your first income entry</p>
                <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Income
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>VAT Type</TableHead>
                    <TableHead className="text-right">Net Excl VAT</TableHead>
                    <TableHead className="text-right">VAT</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIncome.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-muted-foreground">
                        {new Date(item.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.description}</p>
                          {item.reference && <p className="text-xs text-muted-foreground">Ref: {item.reference}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {categories.find(c => c.value === item.category)?.label || item.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {vatTypes.find(v => v.value === item.vat_type)?.label || item.vat_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {formatCurrency(item.net_excl_vat || item.amount)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(item.vat_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingIncome ? 'Edit Income' : 'Add Income'}</DialogTitle>
              <DialogDescription>
                {editingIncome ? 'Update the income details' : 'Enter the income details'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (R) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="e.g., Trip payment from ABC Logistics"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>VAT Type *</Label>
                    <Select value={form.vat_type} onValueChange={(value) => setForm({ ...form, vat_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {vatTypes.map(vat => (
                          <SelectItem key={vat.value} value={vat.value}>{vat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      id="reference"
                      value={form.reference}
                      onChange={(e) => setForm({ ...form, reference: e.target.value })}
                      placeholder="Invoice/PO number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle">Vehicle Registration</Label>
                    <Input
                      id="vehicle"
                      value={form.vehicle_registration}
                      onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value })}
                      placeholder="e.g., CA 123-456"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} className="bg-green-600 hover:bg-green-700">
                  {formLoading ? 'Saving...' : (editingIncome ? 'Update' : 'Add Income')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
